// Single shared Axios client for the whole frontend, going forward. All new
// API calls should import this instead of raw axios or a one-off instance —
// src/Components/api.js stays as a narrow legacy helper for its one existing
// HubSpot-direct caller and is not a pattern to copy.
//
// A large part of the app still imports raw `axios` directly instead of this
// client (pre-dating this shared-client convention). Those calls share the
// same default axios module/instance, so attachAuthInterceptors is also
// applied to the bare `axios` import in index.js - otherwise a 401 on any of
// those call sites never triggers forceLogout, silently leaving the user
// stuck on the current page instead of being sent back to that tenant's
// login page.
import axios from "axios";
import { getTenantSubdomain } from "../Hooks/useTenantFromSubdomain";

const forceLogout = () => {
  localStorage.clear();
  window.location.replace("/");
};

// Single in-flight refresh promise shared by every concurrent 401 across
// BOTH the shared client and the raw axios instance - several requests can
// all fail with an expired access token at once (a page firing multiple API
// calls right as the token expires), and without this they'd each
// independently call /refresh-token and race each other's rotation (see
// docs/implementation-plans/2026-09-11-refresh-token.md).
let refreshPromise = null;

const refreshAccessToken = () => {
  if (!refreshPromise) {
    const refreshToken = localStorage.getItem("refreshToken");
    if (!refreshToken) {
      refreshPromise = Promise.reject(new Error("No refresh token available."));
    } else {
      refreshPromise = axios
        .post(`${process.env.REACT_APP_BASE_URL}/auth/refresh-token`, { refreshToken })
        .then((res) => {
          localStorage.setItem("token", res.data.token);
          localStorage.setItem("refreshToken", res.data.refreshToken);
          return res.data.token;
        })
        .finally(() => {
          refreshPromise = null;
        });
    }
  }
  return refreshPromise;
};

export const attachAuthInterceptors = (instance) => {
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (["tenant_mismatch", "not_tenant_member"].includes(error.response?.data?.error)) {
        forceLogout();
        return Promise.reject(error);
      }

      const originalRequest = error.config;
      const signedOut = !localStorage.getItem("token") && !localStorage.getItem("refreshToken");
      if (error.response?.status === 401 && signedOut) {
        return Promise.reject(error);
      }

      if (error.response?.status === 401 && !originalRequest?._retriedAfterRefresh) {
        originalRequest._retriedAfterRefresh = true;
        try {
          const newAccessToken = await refreshAccessToken();
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return instance(originalRequest);
        } catch (refreshError) {
          forceLogout();
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    }
  );
};

// Without a timeout, a hung backend call never rejects client-side — the
// caller's loading state (and whatever depends on it, e.g. a post-save
// reload) waits forever with no error ever surfaced.
const client = axios.create({ baseURL: process.env.REACT_APP_BASE_URL, timeout: 120000 });

client.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const tenantId = localStorage.getItem("currentTenantId");
  if (tenantId) config.headers["X-Tenant-Id"] = tenantId;
  // The backend can't see the browser's address-bar host (the API call goes
  // to a fixed backend host/port, not wherever the frontend happens to be
  // served from) — so subdomain-aware endpoints (currently: tenant theme)
  // need it passed explicitly.
  const subdomain = getTenantSubdomain();
  if (subdomain) config.headers["X-Tenant-Subdomain"] = subdomain;
  return config;
});

attachAuthInterceptors(client);

export default client;
